import {
  identifierFromKey,
  parseDateOnly,
  startOfDay,
} from '../../lib/assigned-queue';
import { formatDisplayDate, formatFriendlyDate, lateText } from '../../lib/dates';
import StatusPill from './StatusPill';
import { projectColor } from '../../lib/project-color';
import { PRIORITY_LABELS } from '../../lib/service-requests';
import { memberDisplayName, memberGivenName, workItemSubtypeLabel, workItemTypeLabel, type LabelDto, type MemberDto, type WorkItemSubtypeDefinition } from '../../lib/work-types';
import { cn } from '../../lib/utils';
import ControlLink from './ControlLink';
import {
  formatDateRange,
  labelChipStyle,
  MemberAvatar,
  selectedLabels,
  selectedMembers,
} from './WorkCardMeta';

const PRIORITIES = ['urgent', 'high', 'medium', 'low', 'none'] as const;
type Priority = (typeof PRIORITIES)[number];

function isPriority(value: string): value is Priority {
  return (PRIORITIES as readonly string[]).includes(value);
}

function isHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

export function TypeLabel({ type }: { type: string }) {
  if (type === 'task' || type === '') return null;
  return (
    <span className="text-xs font-medium capitalize text-muted-foreground">
      {workItemTypeLabel(type)}
    </span>
  );
}

export function SubtypeLabel({
  subtype,
  subtypes,
  className,
}: {
  subtype?: string | null;
  subtypes?: WorkItemSubtypeDefinition[];
  className?: string;
}) {
  if (!subtype) return null;
  const label = workItemSubtypeLabel(subtype, subtypes);
  return (
    <span className={cn('text-xs font-medium text-muted-foreground', className)} title={label}>
      {label}
    </span>
  );
}

export function PriorityLabel({ priority, type }: { priority: string; type?: string | null }) {
  if (!isPriority(priority) || priority === 'none') return null;
  const serviceRequest = type === 'service_request';
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium',
        !serviceRequest && 'capitalize',
        priority === 'urgent' && 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
        priority === 'high' && 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
        priority === 'medium' && 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300',
        priority === 'low' && 'bg-muted text-muted-foreground',
      )}
    >
      {serviceRequest ? PRIORITY_LABELS[priority] : priority}
    </span>
  );
}

export function WorkAccent({ seed }: { seed: string }) {
  return (
    <span
      className="h-4 w-0.5 shrink-0 rounded-full"
      style={{ backgroundColor: projectColor(seed) }}
      aria-hidden
    />
  );
}

export function WorkKeyCell({
  workItemId,
  workItemKey,
  projectId,
}: {
  workItemId: string;
  workItemKey: string;
  projectId: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <WorkAccent seed={projectId} />
      <WorkItemKeyLink
        workItemId={workItemId}
        workItemKey={workItemKey}
        projectId={projectId}
      />
    </div>
  );
}

/**
 * Mirrors the board columns: the dot carries the state's colour and the name
 * carries the meaning, so a user-picked colour can never hurt the text's
 * contrast the way colouring the label itself did.
 */
export function StateLabel({
  name,
  color,
  group,
  blocked,
}: {
  name: string;
  color?: string;
  /** When known, the plain status leads and the stream's step follows. */
  group?: string;
  blocked?: boolean;
}) {
  if (group) return <StatusPill state={{ name, group }} blocked={blocked} />;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium">
      {color && isHexColor(color) ? (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
      ) : null}
      {name}
    </span>
  );
}

export function WorkItemKeyLink({
  workItemId,
  workItemKey,
  projectId,
}: {
  workItemId: string;
  workItemKey: string;
  projectId: string;
}) {
  return (
    <ControlLink
      workItemId={workItemId}
      workItemKey={workItemKey}
      projectId={projectId}
      className="inline-flex whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground hover:text-foreground hover:no-underline"
    >
      {workItemKey}
    </ControlLink>
  );
}

export function WorkItemTitleLink({
  workItemId,
  workItemKey,
  projectId,
  title,
}: {
  workItemId: string;
  workItemKey: string;
  projectId: string;
  title: string;
}) {
  return (
    <ControlLink
      workItemId={workItemId}
      workItemKey={workItemKey}
      projectId={projectId}
      className="text-sm font-medium text-foreground hover:text-primary hover:no-underline"
    >
      {title}
    </ControlLink>
  );
}

export function SubtaskHint({
  projectId,
  parentId,
  parentKey,
  childCount,
}: {
  projectId: string;
  parentId: string | null;
  parentKey?: string;
  childCount?: number;
}) {
  if (parentId) {
    return (
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Subtask of{' '}
        {parentKey ? (
          <WorkItemKeyLink workItemId={parentId} workItemKey={parentKey} projectId={projectId} />
        ) : (
          'parent'
        )}
      </p>
    );
  }
  if (childCount && childCount > 0) {
    return (
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        {childCount} {childCount === 1 ? 'subtask' : 'subtasks'}
      </p>
    );
  }
  return null;
}

/**
 * Project a work item belongs to, for tables that mix projects. Falls back to the
 * key's identifier (the `OPS` in `OPS-1`) when the project is outside the fetched
 * page of projects.
 */
export function ProjectNameCell({
  name,
  workItemKey,
  projectId,
}: {
  name: string | undefined;
  workItemKey: string;
  projectId?: string;
}) {
  const label = name ?? identifierFromKey(workItemKey);
  if (!label) return <span className="text-muted-foreground/50">-</span>;
  return (
    <span className="flex min-w-0 items-center gap-2" title={label}>
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: projectColor(projectId ?? label) }}
        aria-hidden
      />
      <span className="truncate text-xs text-muted-foreground">{label}</span>
    </span>
  );
}

export function DueDateCell({ value, done = false }: { value: string | null; done?: boolean }) {
  if (!value) {
    return <span className="text-muted-foreground/50">-</span>;
  }
  const parsed = parseDateOnly(value);
  if (!parsed) {
    return <span className="text-muted-foreground/50">-</span>;
  }
  const today = startOfDay(new Date());
  const late = done ? null : lateText(value, today);
  return (
    <span className="flex flex-col text-xs leading-4" title={formatDisplayDate(parsed)}>
      <span className={cn(late ? 'font-medium text-foreground' : 'text-muted-foreground')}>
        {formatFriendlyDate(parsed, today)}
      </span>
      {late ? <span className="font-medium text-red-700 dark:text-red-300">{late}</span> : null}
    </span>
  );
}

/** Start and due dates as one locale-formatted range with year, matching the board cards. */
export function DateRangeCell({
  start,
  due,
  done = false,
}: {
  start: string | null;
  due: string | null;
  done?: boolean;
}) {
  if (due) return <DueDateCell value={due} done={done} />;
  const range = formatDateRange(start, due);
  if (!range) return <span className="text-muted-foreground/50">-</span>;
  return <span className="whitespace-nowrap text-xs text-muted-foreground">Starts {range.text}</span>;
}

/**
 * Overlapping avatars. Each carries the member's email as its accessible name,
 * so the tone and initials are never the only way to tell people apart.
 */
export function AssigneesCell({
  assigneeIds,
  members,
}: {
  assigneeIds: string[];
  members: MemberDto[];
}) {
  const assignees = selectedMembers(assigneeIds, members);
  const none = <span className="text-xs text-muted-foreground/70">No owner</span>;
  if (assignees.length === 0) return none;
  const lead = assignees[0];
  if (!lead) return none;
  const extra = assignees.length - 1;
  const names = assignees.map((member) => memberDisplayName(member)).join(', ');
  return (
    <div className="flex min-w-0 items-center gap-1.5" title={names}>
      <div className="flex shrink-0 -space-x-1.5" aria-hidden>
        {assignees.slice(0, 2).map((member) => (
          <MemberAvatar key={member.user_id} member={member} className="border-2 border-background" />
        ))}
      </div>
      <span className="truncate text-xs text-muted-foreground">
        {memberGivenName(lead)}
        {extra > 0 ? ` +${extra}` : ''}
      </span>
    </div>
  );
}

/** Named label chips, so the colour is reinforced by text rather than replacing it. */
export function LabelsCell({
  labelIds,
  labels,
}: {
  labelIds: string[];
  labels: LabelDto[];
}) {
  const selected = selectedLabels(labelIds, labels);
  if (selected.length === 0) return <span className="text-muted-foreground/50">-</span>;
  const shown = selected.slice(0, 2);
  const rest = selected.slice(shown.length);
  return (
    <div className="flex items-center gap-1">
      {shown.map((label) => (
        <span
          key={label.id}
          title={label.name}
          style={labelChipStyle(label.color)}
          className="max-w-[5rem] truncate rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium"
        >
          {label.name}
        </span>
      ))}
      {rest.length > 0 ? (
        <span
          title={rest.map((label) => label.name).join(', ')}
          className="text-[11px] tabular-nums text-muted-foreground"
        >
          +{rest.length}
        </span>
      ) : null}
    </div>
  );
}

export function MonoKey({ children }: { children: string }) {
  return <span className="font-mono text-xs tabular-nums text-muted-foreground">{children}</span>;
}

export const titleLinkClass =
  'text-sm font-medium text-foreground hover:text-primary hover:no-underline';
